import { useState, useCallback, useEffect } from "react";
import { useWatch } from "react-hook-form";
import { useAddTaskMutation } from "@/modules/tasks/hooks/add-task/use-add-task-mutation.hook";
import { useAddTaskFormSetup } from "@/modules/tasks/hooks/add-task/use-form";
import { useFetchLeads } from "@/modules/tasks/hooks/add-task/use-fetch-leads.hook";
import { useFetchProperties } from "@/modules/tasks/hooks/add-task/use-fetch-properties.hook";
import useUsersFilterOptions from "@/modules/common/hooks/lookups/users-filter-options.hook";
import { LinkToOptions } from "@/modules/tasks/enums";
import {
  getTaskTypeOptions,
  getLinkToOptions,
  getUsersOptions,
} from "@/modules/tasks/hooks/add-task/options";
import {
  FORM_STATE_DEFAULTS,
  TASK_FORM_DEFAULTS,
} from "@/modules/tasks/constants";
import { useAddTaskHandlers } from "@/modules/tasks/hooks/add-task/use-add-task-handlers.hook";
import { FormState, LinkedEntityParams } from "@/modules/tasks/types";

interface UseAddTaskFormProps {
  alwaysOpen?: boolean;
  prefilledAssignee?: {
    uid: string;
    name: string;
  };
  prefilledLead?: {
    uid: string;
    nanoId: string;
    name: string;
  };
  prefilledProperty?: {
    uid: string;
  };
}

export function useAddTaskForm({
  alwaysOpen = false,
  prefilledAssignee,
  prefilledLead,
  prefilledProperty,
}: UseAddTaskFormProps = {}) {
  // ===== STATE MANAGEMENT =====
  const [open, setOpen] = useState(alwaysOpen);
  const [isSubmittingTask, setIsSubmittingTask] = useState(false);

  // Extract stable values for dependency tracking
  const prefilledAssigneeUid = prefilledAssignee?.uid;

  const [formState, setFormState] = useState<FormState>({
    ...FORM_STATE_DEFAULTS,
    dueDate: null,
    linkTo: null,
    linkToEntity: "",
    selectedTime: "",
    selectedType: "",
  });

  // ===== FORM SETUP =====
  const {
    handleSubmitAddTask,
    resetAddTask,
    addTaskErrors,
    controlAddTask,
    setValueAddTask,
    getValuesAddTask,
  } = useAddTaskFormSetup();

  // Watch for selected assignee (userUId)
  const selectedUserUId = useWatch({
    control: controlAddTask,
    name: "userUId",
  });

  // Reset Link To fields when assignee is removed
  useEffect(() => {
    if (!selectedUserUId) {
      setFormState((prev) => ({
        ...prev,
        linkTo: null,
        linkToEntity: "",
      }));
      setValueAddTask("linkedEntities", []);
    }
  }, [selectedUserUId, setValueAddTask]);

  // Handle prefilled values when modal opens
  useEffect(() => {
    if (open) {
      if (prefilledAssignee) {
        setValueAddTask("userUId", prefilledAssignee.uid);
      }

      if (prefilledLead) {
        setFormState((prev) => ({
          ...prev,
          linkTo: LinkToOptions.LEAD,
          linkToEntity: prefilledLead.uid,
        }));

        const linkedEntity = {
          name: `${prefilledLead.name} - ${prefilledLead.nanoId}`,
          systemUId: prefilledLead.nanoId,
          displayName: `${prefilledLead.name} - ${prefilledLead.nanoId}`,
          metaData: { id: prefilledLead.nanoId, type: "lead" },
        };

        setValueAddTask("linkedEntities", [linkedEntity]);
      }

      if (prefilledProperty) {
        setFormState((prev) => ({
          ...prev,
          linkTo: LinkToOptions.PROPERTY,
          linkToEntity: prefilledProperty.uid,
        }));

        const linkedEntity = {
          name: prefilledProperty.uid,
          systemUId: prefilledProperty.uid,
          displayName: prefilledProperty.uid,
          metaData: { id: prefilledProperty.uid, type: "property" },
        };

        setValueAddTask("linkedEntities", [linkedEntity]);
      }
    }
  }, [
    open,
    prefilledAssignee,
    prefilledLead,
    prefilledProperty,
    setValueAddTask,
  ]);

  // Update assignee when it changes (even if modal is closed or open)
  // This ensures the form always has the latest assignee when modal opens
  useEffect(() => {
    if (open && prefilledAssigneeUid) {
      setValueAddTask("userUId", prefilledAssigneeUid);
    }
  }, [prefilledAssigneeUid, open, setValueAddTask]);

  // ===== STATE SETTERS =====
  const updateFormState = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setFormState((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const setLinkToEntity = useCallback(
    (value: string) => updateFormState("linkToEntity", value),
    [updateFormState]
  );
  const setDueDate = useCallback(
    (date: Date | null) => updateFormState("dueDate", date),
    [updateFormState]
  );
  const setSelectedTime = useCallback(
    (time: string) => updateFormState("selectedTime", time),
    [updateFormState]
  );
  const setLinkTo = useCallback(
    (linkTo: LinkToOptions) => {
      updateFormState("linkTo", linkTo);
      if (!linkTo) {
        setValueAddTask("linkedEntities", []);
        setFormState((prev) => ({ ...prev, linkToEntity: "" }));
      }
    },
    [updateFormState, setValueAddTask]
  );

  const createLinkedEntity = useCallback(
    (params: LinkedEntityParams) => ({
      name: params.name,
      systemUId: params.systemUId,
      displayName: params.name,
      metaData: { id: params.id, type: params.type.toLowerCase() },
    }),
    []
  );

  // Reset function
  const resetFormState = useCallback(() => {
    resetAddTask(TASK_FORM_DEFAULTS);
    setFormState({
      ...FORM_STATE_DEFAULTS,
      dueDate: null,
      linkTo: null,
    });
    setIsSubmittingTask(false);
  }, [resetAddTask]);

  // ===== DATA FETCHING - LEADS =====
  const {
    isLoading: leadsLoading,
    leadOptions,
    handleLeadsSearch,
    handleLeadChange,
  } = useFetchLeads(
    open,
    setValueAddTask,
    setLinkToEntity,
    createLinkedEntity,
    formState?.linkTo,
    selectedUserUId
  );

  // ===== DATA FETCHING - PROPERTIES =====
  const {
    isLoading: propertiesLoading,
    propertyOptions,
    handlePropertiesSearch,
    handlePropertyChange,
    selectedProperty,
  } = useFetchProperties(
    open,
    formState?.linkTo,
    setValueAddTask,
    createLinkedEntity,
    selectedUserUId
  );

  // ===== DATA FETCHING - USERS =====
  const {
    usersFilterOptions: assignedUsers,
    handleSearchChange: handleUsersSearch,
    usersFilterOptionsLoading,
  } = useUsersFilterOptions({});

  // ===== MUTATIONS =====
  const { mutationAddTask, isLoading } = useAddTaskMutation({
    setIsSubmittingTask,
    resetFormState,
    setOpen,
  });

  // ===== HANDLERS =====
  const { onSubmitAddTask, handleDateChange, handleTimeChange } =
    useAddTaskHandlers({
      setValueAddTask,
      mutationAddTask,
      setDueDate,
      setSelectedTime,
      selectedTime: formState.selectedTime,
      dueDate: formState.dueDate,
    });

  // ===== STATIC OPTIONS =====
  const taskTypeOptions = getTaskTypeOptions();
  const linkToOptions = getLinkToOptions();

  // Add prefilled values to options if they don't exist
  const usersOptions = getUsersOptions({ usersFilterOptions: assignedUsers });
  if (
    prefilledAssignee &&
    !usersOptions.some((u: any) => u.value === prefilledAssignee.uid)
  ) {
    usersOptions.unshift({
      label: prefilledAssignee.name,
      value: prefilledAssignee.uid,
    } as any);
  }

  const leadsOptionsWithPrefill = [...leadOptions];
  if (
    prefilledLead &&
    !leadOptions.some((l: any) => l.value === prefilledLead.uid)
  ) {
    leadsOptionsWithPrefill.unshift({
      label: `${prefilledLead.name} - ${prefilledLead.nanoId}`,
      value: prefilledLead.uid,
    } as any);
  }

  const propertiesOptionsWithPrefill = [...propertyOptions];

  if (
    prefilledProperty &&
    !propertyOptions.some((p: any) => p.value === prefilledProperty.uid)
  ) {
    propertiesOptionsWithPrefill.unshift({
      label: prefilledProperty.uid,
      value: prefilledProperty.uid,
    } as any);
  }

  // ===== RETURN OBJECT =====
  return {
    // Modal state
    modal: {
      open,
      setOpen,
    },

    // Form submission and validation
    form: {
      handleSubmit: handleSubmitAddTask,
      onSubmit: onSubmitAddTask,
      control: controlAddTask,
      errors: addTaskErrors,
      isSubmitting: isSubmittingTask,
      isLoading: isLoading,
      reset: resetFormState,
      setValue: setValueAddTask,
      getValues: getValuesAddTask,
    },

    // Current state values
    state: {
      type: formState.selectedTime,
      dueDate: formState.dueDate,
      selectedTime: formState.selectedTime,
      linkTo: formState.linkTo,
      linkToEntity: formState.linkToEntity,
      selectedProperty: selectedProperty,
    },

    // State setters
    setters: {
      setDueDate,
      setSelectedTime,
      setLinkTo,
      setLinkToEntity,
    },

    // Dropdown options
    options: {
      taskTypes: taskTypeOptions,
      linkToTypes: linkToOptions,
      users: usersOptions,
      properties: propertyOptions,
      leads: leadsOptionsWithPrefill,
    },

    // Loading states
    loading: {
      leads: leadsLoading,
      properties: propertiesLoading,
      users: usersFilterOptionsLoading,
    },

    // Event handlers
    handlers: {
      onDateChange: handleDateChange,
      onTimeChange: handleTimeChange,
      onPropertyChange: handlePropertyChange,
      onLeadChange: handleLeadChange,
      onLeadsSearch: handleLeadsSearch,
      onPropertiesSearch: handlePropertiesSearch,
      onUsersSearch: handleUsersSearch,
    },
  };
}
